import { useAuth } from "@/core/components/auth/store/auth";
import { Card, CardContent, CardHeader } from "@/core/components/ui/card";
import { productTypeIds } from "@/core/utils";
import { DashboardFilters } from "@/dashboard/types";
import { ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { BookingsTimeseriesChartError } from "./components/bookings-timeseries-error";
import { BookingsTimeseriesChartSkeleton } from "./components/bookings-timeseries-skeleton";
import CustomDot from "./components/custom-dot";
import { TooltipBookingsTimesSeries } from "./components/tooltip-bookings-times-series";
import { useBookingsTimeSeries } from "./hooks/useBookingsTimeSeries";
import { formatMonthLabel } from "./utils";
import { useDashboardFilters } from "@/dashboard/store/useDashboardFilters";
import { useMemo } from "react";
import { DashboardEmptyState } from "../dashboard-empty-state";

export const DashboardBookingsTimeseriesChart = () => {
  const [t] = useTranslation(["dashboard"]);
  const { filters } = useDashboardFilters();

  const { userInfo } = useAuth();

  const { data, isFetching, error } = useBookingsTimeSeries({
    start_date: filters.startDate!,
    end_date: filters.endDate!,
    city: filters.cities ?? [],
    groups: filters.group_id ? [filters.group_id.toString()] : [],
    products: filters.type ?? [],
    spaces: filters.spaces?.map((space) => space.id) ?? [],
    companyId: userInfo.user.leagues_id[0],
    type: filters.type
      ? (filters.type.map((type) => String(productTypeIds[type])) as string[])
      : [],
    status: filters.status,
    users: filters.users?.map((user) => user.user?.id).filter(Boolean),
    neighborhoods: filters.neighborhoods,
  });

  const verifyEmptyData = useMemo(() => {
    return !isFetching && !error && (!data || data.length === 0);
  }, [data, isFetching, error]);

  if (isFetching) {
    return <BookingsTimeseriesChartSkeleton />;
  }

  if (verifyEmptyData) {
    return (
      <Card className="rounded-2xl border-none shadow-sm">
        <CardHeader className="pb-6 border-b border-secondary-500">
          <div className="flex justify-between items-center">
            <div className="flex gap-2 items-center text-darkgray-900">
              {t("dashboard-bookings-timeseries-title")}
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-0 py-6">
          <DashboardEmptyState />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return <BookingsTimeseriesChartError error={error} />;
  }

  return (
    <Card className="bg-card border border-border shadow-sm flex-1">
      <div className="flex items-center justify-between px-6 h-[56px]">
        <h2 className="text-base font-normal text-foreground">
          {t("dashboard-bookings-timeseries-title")}
        </h2>
      </div>

      <hr />

      <div className="w-full h-[300px] p-6">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{ top: 30, right: 20, left: 20, bottom: 5 }}
          >
            <CartesianGrid
              strokeDasharray="0"
              stroke="hsl(var(--border))"
              vertical={false}
            />
            <XAxis
              dataKey="label"
              axisLine={false}
              tickLine={false}
              tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 14 }}
              dy={10}
              tickFormatter={(value) => formatMonthLabel(value, false, t)}
            />

            <YAxis hide />

            <Tooltip
              contentStyle={{
                backgroundColor: "hsl(var(--card))",
                border: "1px solid hsl(var(--border))",
                borderRadius: "var(--radius)",
              }}
              labelStyle={{ color: "hsl(var(--foreground))" }}
              labelFormatter={(value) => formatMonthLabel(value, false, t)}
              content={<TooltipBookingsTimesSeries active={true} data={data} />}
            />

            <Line
              type="natural"
              dataKey="value"
              stroke="hsl(var(--ocean))"
              strokeWidth={2.5}
              dot={<CustomDot />}
              activeDot={{ r: 6, fill: "hsl(var(--ocean))" }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
};
